import { useState, useEffect, useRef } from "react";
import * as React from "react";
import { Save, X, Camera, Mic, Loader2, CheckCircle2, AlertTriangle, AlertOctagon, HelpCircle, Link as LinkIcon, PowerOff, ChevronLeft, ChevronRight, Lock, Play, Trash2 } from "lucide-react";
import api, { formatErr } from "../../lib/api";
import { toast } from "../ui/sonner";
import { uploadFile, uploadUrl } from "../../lib/uploads";
import useVoiceCapture from "../../lib/useVoiceCapture";
import PhotoPickerButton from "../PhotoPickerButton";
import DictTextarea from "../DictTextarea";

/**
 * PerfCapturePanel — Iter 367 (Performance Phase B)
 *
 * Digital "daily log sheet" for one piece of equipment.
 * Renders all parameters on ONE screen (grid entry, per user pref).
 * Each row lets the tech / portal user enter a value, optional notes,
 * attach photos (R2), and dictate voice notes (Whisper).
 *
 * Props:
 *   equipment: { id, name, ... }
 *   parameters: array of Parameter docs (sorted by display_order)
 *   onSaved: called after successful save with the new bundle response
 *   canCapture: boolean — false for portal users w/o capture grant
 */
export default function PerfCapturePanel({ equipment, parameters, onSaved, canCapture = true, site = null, onPrevEquipment = null, onNextEquipment = null, prevEquipmentName = null, nextEquipmentName = null }) {
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  // Iter 389 — Same-day resume: when the user opens the capture panel
  // and today already has a partial bundle, prompt Continue vs New.
  const [existingSession, setExistingSession] = useState(null); // {bundle_id, count, captured_param_ids...}
  const [resumeBundleId, setResumeBundleId] = useState(null);   // active bundle_id if resuming
  // Iter 389 — guardrail: suspicious values (0 or >50% out of target)
  // that trigger a confirmation modal before save.
  const [suspicious, setSuspicious] = useState(null); // {items: [...], onConfirm, onSkip}
  // Iter 391b — Site-wide completion aggregate (not per-equipment).
  const [siteAggregate, setSiteAggregate] = useState(null); // {total, captured, pct}

  // Iter 414 (Dirk, 2026-02-28) — Removed the locked/capturing gate.
  // The old design required a "Start capturing" click before typing
  // was allowed, and any keystrokes typed before that click were
  // silently dropped (data-loss root cause). The form is now always
  // editable; the draft autosave still fires every 1.5s so no work
  // is ever lost.
  const [captureMode, setCaptureMode] = useState("capturing"); // legacy field kept "capturing" so downstream gates never block
  const [draft, setDraft] = useState(null); // server-side draft or null
  const [draftLoading, setDraftLoading] = useState(true);
  const [showAbortModal, setShowAbortModal] = useState(false); // exit-confirm
  // Iter 414 — Persistent autosave status pill state.
  //   "idle"    — no keystrokes since last successful save
  //   "typing"  — user pressing keys, debounce not elapsed yet
  //   "saving"  — network request in flight
  //   "saved"   — last save succeeded (with timestamp)
  //   "offline" — save failed (usually network)
  const [saveStatus, setSaveStatus] = useState({ state: "idle", at: null, error: null });
  // Iter 414.b (Dirk, 2026-02-28) — Tap-committed-row → confirm → edit
  // request flow. `editRequestTarget` = { param, capturedInfo } when
  // the user taps a locked value input.
  const [editRequestTarget, setEditRequestTarget] = useState(null);
  const [editStep, setEditStep] = useState("confirm"); // "confirm" | "form"
  const dirtyRef = useRef(false); // any unsaved keystroke since last commit
  const draftSaveTimerRef = useRef(null);
  // Iter 378 — allow backdating captures. Default is "now"; user can
  // pick any past datetime. Serialised on save as `reading_date` (ISO).
  const [capturedAt, setCapturedAt] = useState(() => {
    const d = new Date();
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
  });
  // Iter 374 — memo lookup for dep display
  const parameterById = React.useMemo(() => {
    const m = {};
    parameters.forEach((p) => { m[p.id] = p; });
    return m;
  }, [parameters]);

  // Track equipment id so we only reset the form when the user switches
  // equipment — NOT when the parent re-fetches `parameters` (which would
  // wipe the "saved" banner immediately after a successful save).
  const prevEqIdRef = useRef(equipment?.id);
  useEffect(() => {
    if (prevEqIdRef.current === equipment?.id && Object.keys(rows).length) return;
    prevEqIdRef.current = equipment?.id;
    const init = {};
    parameters.forEach((p) => {
      init[p.id] = { value: "", notes: "", photo_ids: [] };
    });
    setRows(init);
    setErr("");
    setOk("");
    setResumeBundleId(null);
    setExistingSession(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipment?.id, parameters.length]);

  // Iter 389 — Check for same-day existing capture bundle. If found,
  // prompt Continue (append remaining params) vs Start new.
  useEffect(() => {
    if (!equipment?.id || !canCapture) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/perf/readings/today?equipment_id=${equipment.id}`);
        if (!alive) return;
        if (data?.has_session) {
          setExistingSession(data);
          // Iter 400.20 → Iter 401.5 — Pre-fill rows with today's
          // already-captured values (across ALL bundles for the day,
          // keyed by parameter). Hardened:
          //   • Defensively creates the row entry when it doesn't exist
          //     yet (fixes a race where the /perf/readings/today fetch
          //     resolved before the parameters init effect ran and every
          //     pre-fill got skipped by `if (!next[pid]) return;`).
          //   • Purely presentational: a subsequent Save from this user
          //     still creates a fresh bundle for their own changes.
          if (data.captured_by_param && Object.keys(data.captured_by_param).length) {
            setRows((s) => {
              const next = { ...s };
              Object.entries(data.captured_by_param).forEach(([pid, cap]) => {
                const cur = next[pid] || { value: "", notes: "", photo_ids: [] };
                // Only overwrite if the FE row is still empty — don't
                // stomp on a value the current user just typed.
                if (cur.value === "" || cur.value === null || cur.value === undefined) {
                  next[pid] = {
                    value: cap.value ?? "",
                    notes: cap.notes || "",
                    photo_ids: cap.photo_upload_ids || [],
                  };
                } else {
                  next[pid] = cur;
                }
              });
              return next;
            });
          }
        } else {
          setExistingSession(null);
        }
      } catch {
        /* ignore — resume is optional */
      }
    })();
    return () => { alive = false; };
  }, [equipment?.id, canCapture]);

  // Iter 391b — Fetch SITE-WIDE completion for today (not per-equipment).
  // Refreshes whenever the user switches equipment OR saves readings.
  const [siteAggReloadKey, setSiteAggReloadKey] = useState(0);
  useEffect(() => {
    if (!site?.id) return;
    let alive = true;
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const { data } = await api.get(`/perf/site/${site.id}/completion?date=${today}`);
        if (alive) setSiteAggregate(data);
      } catch {
        /* ignore */
      }
    })();
    return () => { alive = false; };
  }, [site?.id, equipment?.id, siteAggReloadKey]);

  // Iter 414 (Dirk, 2026-02-28) — Fetch server-side draft (if any) for
  // this equipment/day. Runs on equipment switch. AUTO-HYDRATES rows
  // from the draft immediately (no more "Start capturing" / "Continue
  // draft" gate). This is critical: the old two-button gate was the
  // source of the reported data loss because users could type into
  // visible fields while `captureMode === "locked"` and every
  // keystroke was silently dropped.
  useEffect(() => {
    if (!equipment?.id || !canCapture) {
      setDraftLoading(false);
      return;
    }
    let alive = true;
    setDraft(null);
    setDraftLoading(true);
    dirtyRef.current = false;
    setSaveStatus({ state: "idle", at: null, error: null });
    (async () => {
      try {
        const { data } = await api.get(`/perf/drafts?equipment_id=${equipment.id}`);
        if (!alive) return;
        const hasDraft = data && Array.isArray(data.lines) && data.lines.length > 0;
        if (hasDraft) {
          setDraft(data);
          _hydrateFromDraft(data);
          setSaveStatus({ state: "saved", at: data.updated_at, error: null });
        } else {
          setDraft(null);
        }
      } catch {
        if (alive) setDraft(null);
      } finally {
        if (alive) setDraftLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [equipment?.id, canCapture]);

  // Iter 418 (Dirk, 2026-02-28) — Stripped-down UX. The old
  // beforeunload guard + debounced-draft-autosave + save-status pill
  // are gone. We now commit one line at a time on the value input's
  // blur event (see commitOne). Users never see "Save readings" or
  // "Not saved" — every completed value auto-commits and the row
  // turns LOGGED (green) inline.
  const saveDraftNow = React.useCallback(async () => { /* no-op */ }, []);

  // Iter 396 — hydrate rows from a draft (for "Continue draft" path).
  const _hydrateFromDraft = (d) => {
    if (!d || !Array.isArray(d.lines)) return;
    setRows((s) => {
      const next = { ...s };
      d.lines.forEach((ln) => {
        if (next[ln.parameter_id]) {
          next[ln.parameter_id] = {
            value: ln.value ?? "",
            notes: ln.notes || "",
            photo_ids: ln.photo_ids || [],
            // Iter 414 — carry skip state across autosave round-trips.
            skipped: !!ln.skipped,
            skip_reason: ln.skip_reason || "",
          };
        }
      });
      return next;
    });
  };

  const startFreshCapture = () => {
    setCaptureMode("capturing");
    dirtyRef.current = false;
  };

  const continueDraftCapture = () => {
    _hydrateFromDraft(draft);
    setCaptureMode("capturing");
    dirtyRef.current = false;
  };

  const discardDraftAndStart = async () => {
    try { await api.delete(`/perf/drafts?equipment_id=${equipment.id}`); } catch { /* noop */ }
    setDraft(null);
    // Reset rows to empty and enter capture mode.
    const init = {};
    parameters.forEach((p) => { init[p.id] = { value: "", notes: "", photo_ids: [] }; });
    setRows(init);
    setCaptureMode("capturing");
    dirtyRef.current = false;
  };

  // Iter 396 — user requested exit-with-abort. If dirty, show modal.
  const requestAbort = () => {
    if (!dirtyRef.current) {
      // No unsaved changes: just re-lock.
      setCaptureMode("locked");
      return;
    }
    setShowAbortModal(true);
  };

  const confirmAbort = async ({ discard }) => {
    setShowAbortModal(false);
    if (discard) {
      try { await api.delete(`/perf/drafts?equipment_id=${equipment.id}`); } catch { /* noop */ }
      setDraft(null);
      const init = {};
      parameters.forEach((p) => { init[p.id] = { value: "", notes: "", photo_ids: [] }; });
      setRows(init);
    } else {
      // Save-and-exit path: flush the draft one more time (belt & braces).
      await saveDraftNow();
    }
    setCaptureMode("locked");
    dirtyRef.current = false;
  };

  // Iter 414 — patchRow no longer gates on captureMode. The old gate
  // silently dropped keystrokes before "Start capturing" was tapped —
  // the #1 cause of missing readings customers were reporting.
  // Now every keystroke is stored + autosaved.
  const patchRow = (pid, patch) => {
    dirtyRef.current = true;
    setRows((s) => ({ ...s, [pid]: { ...s[pid], ...patch } }));
  };

  // Iter 418 (Dirk, 2026-02-28) — Per-row commit. Fires on the value
  // input's blur (or when a skip reason is picked). POSTs one line to
  // /perf/readings, then refreshes `existingSession.captured_by_param`
  // so the row instantly flips to LOGGED (green, read-only). No global
  // save button; no draft flag; no status pill. If the commit fails,
  // we show a toast and keep the value in the input so the user can
  // retry (they just tab out again).
  const [committingPids, setCommittingPids] = useState({}); // {pid: true} while POST in flight
  const commitOne = React.useCallback(async (pid, opts = {}) => {
    if (!equipment?.id) return;
    const row = rows[pid];
    if (!row) return;
    const param = parameters.find((p) => p.id === pid);
    if (!param) return;
    // Guard against re-committing an already-LOGGED row.
    const capturedByParam = existingSession?.captured_by_param || {};
    const alreadyCommitted = !!capturedByParam[pid] && capturedByParam[pid].value !== null;
    if (alreadyCommitted && !opts.force) return;
    const isSkip = !!opts.skipped;
    const rawValue = opts.value !== undefined ? opts.value : row.value;
    if (!isSkip) {
      // Nothing to commit for empty rows.
      if (rawValue === "" || rawValue === null || rawValue === undefined) return;
    }
    // Build the single-line payload.
    const line = isSkip
      ? {
          parameter_id: pid,
          value: null,
          notes: row.notes || null,
          photo_upload_ids: row.photo_ids || [],
          skipped: true,
          skip_reason: opts.skip_reason || row.skip_reason || "",
        }
      : {
          parameter_id: pid,
          value: rawValue,
          raw_value: String(rawValue),
          notes: row.notes || null,
          photo_upload_ids: row.photo_ids || [],
        };
    setCommittingPids((s) => ({ ...s, [pid]: true }));
    try {
      const { data } = await api.post("/perf/readings", {
        equipment_id: equipment.id,
        lines: [line],
        reading_date: capturedAt ? new Date(capturedAt).toISOString() : undefined,
        bundle_id: existingSession?.bundle_id || resumeBundleId || undefined,
      });
      // Refresh today's committed set so the row flips to LOGGED.
      try {
        const { data: t } = await api.get(`/perf/readings/today?equipment_id=${equipment.id}`);
        if (t?.has_session) {
          setExistingSession(t);
          setResumeBundleId(t.bundle_id);
        }
      } catch { /* ignore */ }
      setSiteAggReloadKey((k) => k + 1);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatErr(e) || "Save failed — value kept, tap out to retry");
    } finally {
      setCommittingPids((s) => { const n = { ...s }; delete n[pid]; return n; });
    }
  }, [equipment?.id, rows, parameters, existingSession, resumeBundleId, capturedAt, onSaved]);

  const computeStatus = (value, target) => {
    if (value === "" || value === null || value === undefined || !target || !target.kind) return null;
    if (target.kind === "yes_no") return null;
    const v = Number(value);
    if (Number.isNaN(v)) return null;
    const warn = Number(target.warn_pct ?? 5);
    const alarm = Number(target.alarm_pct ?? 10);
    let diffPct;
    if (target.kind === "exact") {
      const ref = Number(target.value || 0);
      if (!ref) return null;
      diffPct = Math.abs((v - ref) / ref) * 100;
    } else if (target.kind === "min") {
      const ref = Number(target.min || 0);
      diffPct = v >= ref ? 0 : Math.abs((ref - v) / Math.max(ref, 1)) * 100;
    } else if (target.kind === "max") {
      const ref = Number(target.max || 0);
      diffPct = v <= ref ? 0 : Math.abs((v - ref) / Math.max(ref, 1)) * 100;
    } else if (target.kind === "range") {
      const lo = Number(target.min || 0);
      const hi = Number(target.max || 0);
      if (v >= lo && v <= hi) diffPct = 0;
      else if (v < lo) diffPct = Math.abs((lo - v) / Math.max(lo, 1)) * 100;
      else diffPct = Math.abs((v - hi) / Math.max(hi, 1)) * 100;
    } else return null;
    if (diffPct <= warn) return "ok";
    if (diffPct <= alarm) return "warn";
    return "alarm";
  };

  /**
   * Iter 374 — evaluate a parameter's cross-parameter dependencies
   * against the current bundle values. Returns list of {ok, message, ...}
   * so the UI can flag violations live.
   */
  const evalDeps = (param) => {
    const deps = param.dependencies || [];
    if (!deps.length) return [];
    return deps.map((d) => {
      const ref = rows[d.ref_param_id]?.value;
      if (ref === "" || ref === undefined || ref === null || Number.isNaN(Number(ref))) {
        return { ...d, ok: null, ref_value: null };
      }
      const v = Number(rows[param.id]?.value);
      if (Number.isNaN(v)) return { ...d, ok: null, ref_value: Number(ref) };
      const r = Number(ref);
      const thr = Number(d.value ?? 0);
      let ok = true;
      switch (d.op) {
        case "lt":         ok = v < (r + thr); break;
        case "lte":        ok = v <= (r + thr); break;
        case "gt":         ok = v > (r + thr); break;
        case "gte":        ok = v >= (r + thr); break;
        case "eq":         ok = Math.abs(v - (r + thr)) < 1e-9; break;
        case "neq":        ok = Math.abs(v - (r + thr)) >= 1e-9; break;
        case "max_diff":   ok = Math.abs(v - r) <= thr; break;
        case "min_diff":   ok = Math.abs(v - r) >= thr; break;
        case "max_down":   ok = (r - v) <= thr; break;
        case "max_up":     ok = (v - r) <= thr; break;
        case "max_ratio":  ok = v <= r * thr; break;
        case "min_ratio":  ok = v >= r * thr; break;
        default: ok = true;
      }
      return { ...d, ok, ref_value: r };
    });
  };

  /**
   * Iter 389 — Detect suspicious values before save:
   *  • Exactly 0 while target min > 0 (equipment likely off — user
   *    should have skipped instead of typing a placeholder).
   *  • Value >50% outside target bounds (probable typo).
   * Returns array of {param, value, reason} entries so the modal can
   * render a checkbox list.
   */
  const detectSuspicious = (lines) => {
    const out = [];
    const paramByPid = {};
    parameters.forEach((p) => { paramByPid[p.id] = p; });
    for (const ln of lines) {
      const p = paramByPid[ln.parameter_id];
      if (!p) continue;
      const t = p.target || {};
      const v = Number(ln.value);
      if (!Number.isFinite(v)) continue;
      // Rule 1: exact zero on a param with a strictly positive lower bound.
      const lo = t.kind === "min" || t.kind === "range" ? Number(t.min ?? NaN)
        : t.kind === "exact" ? Number(t.value ?? NaN) : NaN;
      if (v === 0 && Number.isFinite(lo) && lo > 0) {
        out.push({ pid: p.id, name: p.name, unit: p.unit, value: v, reason: "zero-off" });
        continue;
      }
      // Rule 2: >50% outside bounds.
      let outsideBy = 0;
      if (t.kind === "exact") {
        const ref = Number(t.value || 0);
        if (ref !== 0) outsideBy = Math.abs((v - ref) / ref) * 100;
      } else if (t.kind === "range") {
        const mn = Number(t.min ?? -Infinity);
        const mx = Number(t.max ?? Infinity);
        if (v < mn && Number.isFinite(mn) && mn !== 0) {
          outsideBy = Math.abs((mn - v) / Math.abs(mn)) * 100;
        } else if (v > mx && Number.isFinite(mx) && mx !== 0) {
          outsideBy = Math.abs((v - mx) / Math.abs(mx)) * 100;
        }
      } else if (t.kind === "min") {
        const mn = Number(t.min ?? 0);
        if (v < mn && mn !== 0) outsideBy = Math.abs((mn - v) / Math.abs(mn)) * 100;
      } else if (t.kind === "max") {
        const mx = Number(t.max ?? 0);
        if (v > mx && mx !== 0) outsideBy = Math.abs((v - mx) / Math.abs(mx)) * 100;
      }
      if (outsideBy > 50) {
        out.push({ pid: p.id, name: p.name, unit: p.unit, value: v, reason: "out-of-range", outsideBy });
      }
    }
    return out;
  };

  const doSave = async (lines) => {
    setBusy(true);
    try {
      const { data } = await api.post("/perf/readings", {
        equipment_id: equipment.id,
        lines,
        reading_date: capturedAt ? new Date(capturedAt).toISOString() : undefined,
        // Iter 389 — Append to the existing same-day bundle when the
        // user chose "Continue session".
        bundle_id: resumeBundleId || undefined,
      });
      setOk(`Saved ${data.saved?.length || 0} reading(s)${data.alerts ? ` · ${data.alerts} alert(s) sent` : ""}`);
      setTimeout(() => setOk(""), 6000);
      // Reset form but keep resumeBundleId if we're mid-session — this
      // way another partial capture still appends to the same bundle.
      const init = {};
      parameters.forEach((p) => {
        init[p.id] = { value: "", notes: "", photo_ids: [] };
      });
      setRows(init);
      // Iter 396 — commit succeeded. Backend deleted the server draft.
      // Re-lock the panel + clear dirty so the user has to explicitly
      // Start again for the next round (mirrors the safety flow).
      setDraft(null);
      setCaptureMode("locked");
      dirtyRef.current = false;
      // Refresh existingSession so the "captured" pill count updates.
      try {
        const { data: t } = await api.get(`/perf/readings/today?equipment_id=${equipment.id}`);
        setExistingSession(t?.has_session ? t : null);
      } catch { /* ignore */ }
      // Iter 391b — Refresh site-wide completion after save.
      setSiteAggReloadKey((k) => k + 1);
      onSaved?.(data);
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setErr("");
    setOk("");
    // Validate mandatory — but a resumed session excludes params that
    // were already captured earlier today (they're not "missing").
    const alreadyPids = resumeBundleId ? new Set(existingSession?.captured_param_ids || []) : new Set();
    const missing = parameters.filter((p) => p.mandatory
      && !alreadyPids.has(p.id)
      && (rows[p.id]?.value === "" || rows[p.id]?.value === undefined));
    if (missing.length) {
      setErr(`Mandatory parameters missing: ${missing.map((m) => m.name).join(", ")}`);
      return;
    }
    const missingPhotos = parameters.filter(
      (p) => p.photo_required && !(rows[p.id]?.photo_ids || []).length,
    );
    if (missingPhotos.length) {
      setErr(`Photo required for: ${missingPhotos.map((m) => m.name).join(", ")}`);
      return;
    }
    // Iter 400.20 — Exclude rows whose value is unchanged from a
    // reading already captured earlier today. Otherwise clicking Save
    // on a panel opened by a second user (with everything pre-filled)
    // would create duplicate rows for the same day.
    const capturedByParam = existingSession?.captured_by_param || {};
    const lines = parameters
      .filter((p) => rows[p.id]?.value !== "" && rows[p.id]?.value !== undefined)
      .filter((p) => {
        const cap = capturedByParam[p.id];
        if (!cap) return true; // brand-new value
        const same =
          Number(cap.value) === Number(rows[p.id].value) &&
          (cap.notes || "") === (rows[p.id].notes || "") &&
          (cap.photo_upload_ids || []).length === (rows[p.id].photo_ids || []).length;
        return !same; // include only if the user actually changed it
      })
      .map((p) => {
        // Iter 415.2 (Dirk, 2026-02-28) — Send raw_value alongside so
        // the backend can strip units ("2L" → 2) AND keep the original
        // audit string. Skipped rows still go through unchanged.
        const raw = rows[p.id];
        if (raw?.skipped) {
          return {
            parameter_id: p.id,
            value: null,
            notes: raw.notes || null,
            photo_upload_ids: raw.photo_ids || [],
            skipped: true,
            skip_reason: raw.skip_reason || "",
          };
        }
        return {
          parameter_id: p.id,
          value: raw.value,               // raw string — backend coerces
          raw_value: String(raw.value),   // audit
          notes: raw.notes || null,
          photo_upload_ids: raw.photo_ids || [],
        };
      });
    if (!lines.length) {
      // Iter 400.20 — friendly message when everything on the panel is
      // already captured today and unchanged: this is a "nothing to do"
      // state, not an error the user should worry about.
      const anyPrefilled = parameters.some((p) => capturedByParam[p.id]);
      setErr(
        anyPrefilled
          ? "Every parameter shown here is already captured today and unchanged. Change a value first, or use the History pencil to request an edit."
          : "Enter at least one value before saving.",
      );
      return;
    }
    // Iter 389 — guardrail: intercept suspicious values (zero / OOR)
    // and force the user to explicitly Confirm or Skip each one.
    const flagged = detectSuspicious(lines);
    if (flagged.length) {
      setSuspicious({
        items: flagged,
        onResolve: (skipPids) => {
          setSuspicious(null);
          const skipSet = new Set(skipPids);
          // Also clear the input rows for skipped params so the UI
          // reflects that they weren't captured.
          if (skipSet.size) {
            setRows((s) => {
              const next = { ...s };
              skipSet.forEach((pid) => {
                if (next[pid]) next[pid] = { ...next[pid], value: "" };
              });
              return next;
            });
          }
          const kept = lines.filter((ln) => !skipSet.has(ln.parameter_id));
          if (!kept.length) {
            setOk("All flagged readings skipped — nothing to save.");
            setTimeout(() => setOk(""), 4000);
            return;
          }
          doSave(kept);
        },
        onCancel: () => setSuspicious(null),
      });
      return;
    }
    await doSave(lines);
  };

  // Iter 418 (Dirk, 2026-02-28) — Prev/Next just navigates. Per-row
  // auto-commit on blur means values are already committed by the
  // time the user taps the nav button (blur fires first). No batch
  // save() call here — that flow no longer exists in the UI.
  const _saveThenNavigate = async (navFn) => {
    if (!navFn) return;
    navFn();
  };

  if (!parameters.length) {
    return (
      <div className="hv-card p-6 text-center text-sm text-[#647087]">
        No parameters configured for <b className="text-[#e6ecff]">{equipment.name}</b> yet.
        Ask an admin to add measurement parameters.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="perf-capture-panel">
      {/* Iter 418 (Dirk, 2026-02-28) — Stripped-down header. Gone:
          SaveStatusPill, AbortDraftModal render, Continue/Start-new
          resume prompt, header Discard + Save readings buttons, the
          Reading date/time picker, and the Continue → next open
          reading button. Committed rows show LOGGED inline; empty
          rows auto-commit on blur. See commitOne(). */}

      {/* Iter 414.b — Edit-request modal chain. Two steps: (1) confirm
          you want to edit a LOCKED reading; (2) fill in new value +
          reason + photo, POST /perf/readings/edit-request. */}
      {editRequestTarget && (
        <ReadingEditRequestModal
          param={editRequestTarget.param}
          capturedInfo={editRequestTarget.capturedInfo}
          step={editStep}
          onProceed={() => setEditStep("form")}
          onClose={() => { setEditRequestTarget(null); setEditStep("confirm"); }}
          onSubmitted={() => { setEditRequestTarget(null); setEditStep("confirm"); }}
        />
      )}

      {/* Iter 389 — Suspicious value guardrail modal (only shown when
          the batch-save flow (below) detects a zero-off / out-of-range
          value). The per-row commit path skips this modal to keep
          the "type and it saves" UX friction-free. */}
      {suspicious && (
        <SuspiciousValueModal
          items={suspicious.items}
          onResolve={suspicious.onResolve}
          onCancel={suspicious.onCancel}
        />
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#647087]">
            Daily log · {equipment.name}
          </div>
          <div className="text-xs text-[#a0aabf] mt-0.5">
            {parameters.length} parameter{parameters.length === 1 ? "" : "s"} · target vs actual · <span className="text-emerald-300/80">values save automatically</span>
          </div>
        </div>
      </div>

      {err && (
        <div className="hv-card border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200" data-testid="perf-capture-err">
          {err}
        </div>
      )}
      {ok && (
        <div className="hv-card border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200" data-testid="perf-capture-ok">
          {ok}
        </div>
      )}

      {/* Iter 385 / Iter 391b / Iter 401.5 — Progress bar now shows
          BOTH per-equipment (primary — the tech's actual TODO list on
          THIS unit) and site-wide (secondary — plant-level snapshot).
          When per-equipment reaches 100% we lock the panel with a
          green LOCKED banner so no user can accidentally override
          captured readings. */}
      {parameters.length > 0 && (() => {
        // Per-equipment "captured" set (from server saved bundles)
        const perEqCapturedIds = new Set(
          Object.keys(existingSession?.captured_by_param || {})
        );
        // Client-side fills layered on top so the bar animates as the
        // tech types.
        const filled = parameters.filter((p) => {
          const v = rows[p.id]?.value;
          return v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v));
        }).length;
        parameters.forEach((p) => {
          const v = rows[p.id]?.value;
          if (v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v))) {
            perEqCapturedIds.add(p.id);
          }
        });
        const perEqTotal = parameters.length;
        const perEqCaptured = Math.min(perEqTotal, perEqCapturedIds.size);
        const perEqPct = perEqTotal ? Math.round((perEqCaptured / perEqTotal) * 100) : 0;
        const perEqComplete = perEqTotal > 0 && perEqCaptured >= perEqTotal;

        const mandatoryTotal = parameters.filter((p) => p.mandatory).length;
        const mandatoryFilled = parameters.filter((p) => {
          if (!p.mandatory) return false;
          const v = rows[p.id]?.value;
          return v !== "" && v !== null && v !== undefined && !Number.isNaN(Number(v));
        }).length;

        // Site-wide layered stats
        const useSite = !!siteAggregate && (siteAggregate.total || 0) > 0;
        const siteTotal = useSite ? siteAggregate.total : perEqTotal;
        const siteCaptured = useSite
          ? Math.min(siteTotal, (siteAggregate.captured || 0) + filled)
          : filled;
        const sitePct = siteTotal ? Math.round((siteCaptured / siteTotal) * 100) : 0;

        const barColor = perEqPct >= 100 ? "#10B981" : perEqPct >= 50 ? "#1FB6C1" : "#F59E0B";
        // First empty parameter — used by the "Continue" button to jump
        // straight to the next unfilled row.
        const firstEmpty = parameters.find((p) => {
          const v = rows[p.id]?.value;
          return v === "" || v === null || v === undefined || Number.isNaN(Number(v));
        });
        const scrollToEmpty = () => {
          if (!firstEmpty) return;
          if (captureMode !== "capturing" && canCapture) startFreshCapture();
          const el = document.querySelector(`[data-testid="perf-param-row-${firstEmpty.id}"]`);
          if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            const input = el.querySelector('input[type="number"], input[type="text"]');
            if (input) setTimeout(() => input.focus(), 350);
          }
        };
        return (
          <div className="hv-card p-3 space-y-2" data-testid="perf-capture-progress">
            <div className="flex items-center justify-between text-[11px] flex-wrap gap-2">
              <span className="text-[#a0aabf]">
                <b className="text-white font-mono">{perEqCaptured}</b> / {perEqTotal} captured on <b className="text-white">{equipment.name}</b>
                {useSite && (
                  <span className="text-[#647087] ml-2">
                    · site-wide <b className="text-white font-mono">{siteCaptured}</b>/{siteTotal} ({sitePct}%)
                  </span>
                )}
                {mandatoryTotal > 0 && (
                  <span className="text-[#647087] ml-2">
                    · mandatory <b className="text-white font-mono">{mandatoryFilled}</b>/{mandatoryTotal}
                  </span>
                )}
              </span>
              <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: barColor }}>
                {perEqPct}% complete
              </span>
            </div>
            <div className="h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full transition-all duration-500"
                style={{ width: `${perEqPct}%`, background: barColor }}
                data-testid="perf-capture-progress-bar"
              />
            </div>
            {mandatoryTotal > 0 && mandatoryFilled < mandatoryTotal && (
              <div className="text-[10px] text-rose-300 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {mandatoryTotal - mandatoryFilled} required parameter{mandatoryTotal - mandatoryFilled === 1 ? "" : "s"} still empty on this equipment
              </div>
            )}
            {/* Iter 418 (Dirk, 2026-02-28) — Removed the "Continue →
                next open reading" button. Every keystroke now
                auto-commits on blur, so there's no need for a jump-
                to-next affordance; the user just scrolls or tabs. */}
            {/* Iter 401.5 — Fully captured LOCKED banner. Any user who
                opens the log after everything's captured sees a big
                green "Complete — locked for today" so they don't
                accidentally re-enter values and clobber the audit
                trail. To change a captured reading, use the History
                pencil on that specific row (goes through the
                edit-request approval flow). */}
            {perEqComplete && (
              <div
                className="mt-1 flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2"
                data-testid="perf-capture-locked-complete-banner"
              >
                <Lock className="w-4 h-4 text-emerald-300 shrink-0" />
                <div className="text-[11px] text-emerald-200 flex-1">
                  <b>Complete for today · locked.</b> All {perEqTotal} parameters on this
                  equipment have been captured. Use the History pencil on a row to submit
                  an audited edit-request if a value needs correcting.
                </div>
              </div>
            )}
          </div>
        );
      })()}

      <div className="hv-card overflow-hidden">
        <div className="hidden md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,2fr)_minmax(0,1.2fr)] gap-3 px-3 py-2 text-[10px] uppercase tracking-wider text-[#647087] border-b border-white/5">
          <div>Parameter</div>
          <div>Target</div>
          <div>Value</div>
          <div>Notes</div>
          <div className="text-right">Attachments</div>
        </div>
        <div className="divide-y divide-white/5">
          {parameters.map((p) => (
            <ParameterRow
              key={p.id}
              param={p}
              paramById={parameterById}
              row={rows[p.id] || { value: "", notes: "", photo_ids: [], skipped: false, skip_reason: "" }}
              onChange={(patch) => patchRow(p.id, patch)}
              status={(() => {
                // A captured reading keeps the grade it got when saved (same as Analytics);
                // only values being typed now are graded live.
                const cap = (existingSession?.captured_by_param || {})[p.id];
                if (cap && cap.status && cap.status !== "skipped" && String(cap.value) === String(rows[p.id]?.value)) {
                  return ["ok", "warn", "alarm"].includes(cap.status) ? cap.status : null;
                }
                return computeStatus(rows[p.id]?.value, p.target);
              })()}
              depResults={evalDeps(p)}
              canCapture={canCapture}
              alreadyCaptured={resumeBundleId && (existingSession?.captured_param_ids || []).includes(p.id)}
              capturedInfo={(existingSession?.captured_by_param || {})[p.id]}
              committing={!!committingPids[p.id]}
              onCommit={(opts) => commitOne(p.id, opts || {})}
              onRequestEdit={(param, capInfo) => {
                // Iter 414.b — Tapping a committed row opens the
                // confirmation modal → EditReadingModal (imported
                // from PerfRegisterView).
                setEditRequestTarget({ param, capturedInfo: capInfo });
              }}
            />
          ))}
        </div>
      </div>

      {/* Iter 391b — Prev/Next equipment nav so techs don't have to
          scroll back to the left rail to move between units. Only
          shown when the parent supplies nav callbacks (i.e. we're
          inside a site with siblings). */}
      {(onPrevEquipment || onNextEquipment) && (
        <div className="hv-card p-2.5 sm:p-3 flex items-center justify-between gap-1.5 sm:gap-2 flex-nowrap" data-testid="perf-capture-eq-nav">
          <button
            type="button"
            onClick={() => _saveThenNavigate(onPrevEquipment)}
            disabled={!onPrevEquipment || busy}
            className="hv-btn hv-btn-ghost text-[11px] sm:text-xs flex items-center gap-1 sm:gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed min-w-0 flex-1 sm:flex-none justify-start"
            data-testid="perf-capture-eq-prev"
          >
            <ChevronLeft className="w-3.5 h-3.5 shrink-0" />
            <span className="flex flex-col items-start leading-tight min-w-0">
              <span className="text-[9px] uppercase tracking-wider text-[#647087]">Previous</span>
              <span className="text-white truncate max-w-[80px] sm:max-w-[160px]">{prevEquipmentName || "—"}</span>
            </span>
          </button>
          <span className="text-[9px] sm:text-[10px] text-[#647087] uppercase tracking-wider text-center truncate max-w-[90px] sm:max-w-[200px] px-1">
            {equipment?.name}
          </span>
          <button
            type="button"
            onClick={() => _saveThenNavigate(onNextEquipment)}
            disabled={!onNextEquipment || busy}
            className="hv-btn hv-btn-ghost text-[11px] sm:text-xs flex items-center gap-1 sm:gap-1.5 disabled:opacity-30 disabled:cursor-not-allowed min-w-0 flex-1 sm:flex-none justify-end"
            data-testid="perf-capture-eq-next"
          >
            <span className="flex flex-col items-end leading-tight min-w-0">
              <span className="text-[9px] uppercase tracking-wider text-[#647087]">Next</span>
              <span className="text-white truncate max-w-[80px] sm:max-w-[160px]">{nextEquipmentName || "—"}</span>
            </span>
            <ChevronRight className="w-3.5 h-3.5 shrink-0" />
          </button>
        </div>
      )}
    </div>
  );
}

function skipReasonLabel(code) {
  // Iter 414.b — preset codes map to human labels, anything else is
  // treated as free-text.
  if (code === "component_standby") return "Component standby";
  if (code === "component_faulty") return "Component faulty";
  return (code || "").trim() || "no reason given";
}


function SkipPicker({ paramId, onSkip }) {
  const [open, setOpen] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [showText, setShowText] = useState(false);
  return (
    <div className="relative mt-1" data-testid={`perf-param-skip-wrap-${paramId}`}>
      {!open && !showText && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-[10px] uppercase tracking-wider text-[#647087] hover:text-amber-300 underline decoration-dotted"
          data-testid={`perf-param-skip-open-${paramId}`}
          title="Couldn't take this reading? Mark it N/A with a reason."
        >
          N/A · reason?
        </button>
      )}
      {open && !showText && (
        <div className="hv-card p-1.5 space-y-0.5 border border-amber-400/30" data-testid={`perf-param-skip-menu-${paramId}`}>
          <button type="button"
            onClick={() => { onSkip("component_standby"); setOpen(false); }}
            className="block w-full text-left text-[11px] px-2 py-1 rounded hover:bg-white/[0.04]"
            data-testid={`perf-param-skip-standby-${paramId}`}>
            Component standby
          </button>
          <button type="button"
            onClick={() => { onSkip("component_faulty"); setOpen(false); }}
            className="block w-full text-left text-[11px] px-2 py-1 rounded hover:bg-white/[0.04]"
            data-testid={`perf-param-skip-faulty-${paramId}`}>
            Component faulty
          </button>
          <button type="button"
            onClick={() => { setOpen(false); setShowText(true); }}
            className="block w-full text-left text-[11px] px-2 py-1 rounded hover:bg-white/[0.04] text-[#a0aabf]"
            data-testid={`perf-param-skip-freetext-${paramId}`}>
            Other reason…
          </button>
          <button type="button"
            onClick={() => setOpen(false)}
            className="block w-full text-left text-[10px] px-2 py-0.5 rounded text-[#647087] hover:text-white"
            data-testid={`perf-param-skip-cancel-${paramId}`}>
            Cancel
          </button>
        </div>
      )}
      {showText && (
        <div className="hv-card p-1.5 space-y-1 border border-amber-400/30">
          <input
            autoFocus
            value={freeText}
            onChange={(e) => setFreeText(e.target.value)}
            placeholder="Describe why…"
            className="hv-input w-full text-xs"
            data-testid={`perf-param-skip-input-${paramId}`}
          />
          <div className="flex gap-1">
            <button type="button"
              onClick={() => {
                const r = freeText.trim();
                if (!r) return;
                onSkip(r);
                setShowText(false);
                setFreeText("");
              }}
              className="hv-btn hv-btn-primary text-[10px] py-0.5 px-1.5 flex-1"
              data-testid={`perf-param-skip-save-${paramId}`}>
              Mark N/A
            </button>
            <button type="button"
              onClick={() => { setShowText(false); setFreeText(""); }}
              className="hv-btn hv-btn-ghost text-[10px] py-0.5 px-1.5"
              data-testid={`perf-param-skip-textcancel-${paramId}`}>
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function ParameterRow({ param, paramById, row, onChange, status, depResults, canCapture, alreadyCaptured, capturedInfo, onRequestEdit, committing = false, onCommit }) {
  const [uploading, setUploading] = useState(false);
  const cap = useVoiceCapture({ context: "performance_reading" });
  useEffect(() => {
    if (cap.result?.refined || cap.result?.raw) {
      const text = (cap.result.refined || cap.result.raw || "").trim();
      if (text) onChange({ notes: (row.notes ? row.notes + "\n" : "") + text });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cap.result]);

  const onPickFiles = async (files) => {
    const fs = Array.from(files || []);
    if (!fs.length) return;
    setUploading(true);
    try {
      const results = await Promise.all(fs.map((f) => uploadFile(f, { source: "performance" })));
      const ids = results.map((r) => r?.id).filter(Boolean);
      onChange({ photo_ids: [...(row.photo_ids || []), ...ids] });
    } finally {
      setUploading(false);
    }
  };

  const targetLabel = (() => {
    const t = param.target || {};
    if (!t.kind) return "—";
    if (t.kind === "exact") return `= ${t.value ?? "?"} ${param.unit || ""}`;
    if (t.kind === "min") return `≥ ${t.min ?? "?"} ${param.unit || ""}`;
    if (t.kind === "max") return `≤ ${t.max ?? "?"} ${param.unit || ""}`;
    if (t.kind === "range") return `${t.min ?? "?"}–${t.max ?? "?"} ${param.unit || ""}`;
    if (t.kind === "yes_no") return "Yes / No";
    return "—";
  })();

  // Iter 414.b + Iter 415.2 (Dirk, 2026-02-28) — Row-level state coloring:
  //   • GREEN border   → parameter has a committed reading with a
  //                      real numeric value today. The value shown
  //                      MUST match `capturedInfo.value` (single source
  //                      of truth). Tapping triggers the edit-request
  //                      modal.
  //   • AMBER border   → parameter is pending. Either no commit at all,
  //                      OR the committed row's value is null (the
  //                      broken "LOGGED but empty" state Dirk spotted).
  //                      In the latter case the badge does NOT lie —
  //                      the row is genuinely pending until re-captured.
  //   • Neutral border → user has typed a draft value that hasn't been
  //                      committed yet.
  //   • Slate/dim      → skipped for today.
  const committedValue = capturedInfo?.value;
  const hasValidCommit = capturedInfo && committedValue !== null && committedValue !== "" && committedValue !== undefined;
  const isCommitted = !!hasValidCommit;
  const isSkipped = !!row.skipped;
  const hasDraftValue = row.value !== "" && row.value !== null && row.value !== undefined;
  const isPending = !isCommitted && !isSkipped && !hasDraftValue;
  const wrapperClass = (() => {
    if (isCommitted) return "border-l-4 border-emerald-500/60 bg-emerald-500/[0.03]";
    if (isSkipped)   return "border-l-4 border-slate-500/40 bg-slate-500/[0.03]";
    if (isPending)   return "border-l-4 border-amber-400/50 bg-amber-500/[0.03]";
    return "border-l-4 border-white/10";
  })();

  return (
    <div
      className={`md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,1.6fr)_minmax(0,1fr)_minmax(0,2fr)_minmax(0,1.2fr)] gap-3 px-3 py-3 items-start rounded-r transition-colors ${wrapperClass}`}
      data-testid={`perf-param-row-${param.id}`}
      data-row-state={isCommitted ? "committed" : isSkipped ? "skipped" : isPending ? "pending" : "draft"}
    >
      <div className="mb-2 md:mb-0">
        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
          {param.name}
          {param.mandatory && <span className="text-[9px] uppercase tracking-wider text-rose-300">*req</span>}
          <StatusPill status={status} />
          {(alreadyCaptured || capturedInfo) && (
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold inline-flex items-center gap-0.5 bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
              data-testid={`perf-param-already-captured-${param.id}`}
              title={
                capturedInfo
                  ? `Captured today by ${capturedInfo.captured_by_name || "?"}${capturedInfo.captured_at ? " at " + capturedInfo.captured_at.slice(11, 16) : ""} — editing here creates a new reading; use the History pencil for an audited edit-request instead.`
                  : "Already captured earlier today."
              }
            >
              <CheckCircle2 className="w-2.5 h-2.5" /> LOGGED
            </span>
          )}
          {(depResults || []).some((d) => d.ok === false) && (
            <span
              className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold inline-flex items-center gap-0.5 bg-[#a78bfa]/15 text-[#a78bfa] border border-[#a78bfa]/40"
              data-testid={`perf-param-dep-viol-${param.id}`}
              title="Cross-parameter dependency violated"
            >
              <LinkIcon className="w-2.5 h-2.5" /> DEP
            </span>
          )}
        </div>
        {capturedInfo && (
          <div
            className="text-[10px] text-emerald-300/80 mt-0.5 truncate"
            title="This row is pre-filled from an earlier capture today. Change it here only if the value is wrong and a NEW reading should replace it."
            data-testid={`perf-param-captured-meta-${param.id}`}
          >
            captured by {capturedInfo.captured_by_name || "?"}
            {capturedInfo.captured_at ? ` · ${capturedInfo.captured_at.slice(11, 16)}` : ""}
            {(capturedInfo.photo_upload_ids || []).length > 0 && (
              <span className="text-[#647087]"> · {capturedInfo.photo_upload_ids.length} photo{capturedInfo.photo_upload_ids.length === 1 ? "" : "s"}</span>
            )}
          </div>
        )}
        {param.description && (
          <div className="text-[11px] text-[#647087] truncate">{param.description}</div>
        )}
        {(depResults || []).length > 0 && (
          <div className="mt-1 space-y-0.5">
            {(depResults || []).map((d, i) => {
              if (d.ok === null) return null;
              const refName = paramById?.[d.ref_param_id]?.name || "ref";
              const cls = d.ok ? "text-emerald-300/70" : "text-rose-300";
              const opTxt = {
                lt: "<", lte: "≤", gt: ">", gte: "≥", eq: "=", neq: "≠",
                max_diff: "|Δ|≤", min_diff: "|Δ|≥", max_ratio: "≤×", min_ratio: "≥×",
              }[d.op] || d.op;
              return (
                <div key={i} className={`text-[10px] ${cls}`} data-testid={`perf-param-dep-hint-${param.id}-${i}`}>
                  {d.ok ? "✓" : "✗"} {refName} {opTxt} {d.value}
                  {d.message ? ` — ${d.message}` : ""}
                  {!d.ok && d.ref_value != null && ` (ref=${d.ref_value})`}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="mb-2 md:mb-0 text-xs text-[#a0aabf] font-mono">{targetLabel}</div>
      <div className="mb-2 md:mb-0">
        {isSkipped ? (
          <div
            className="text-[11px] italic text-slate-400 py-2"
            data-testid={`perf-param-skipped-view-${param.id}`}
            title={`Skipped today · ${skipReasonLabel(row.skip_reason)}`}
          >
            N/A · {skipReasonLabel(row.skip_reason)}{" "}
            <button
              type="button"
              onClick={() => onChange({ skipped: false, skip_reason: "" })}
              className="underline hover:text-white ml-1"
              data-testid={`perf-param-unskip-${param.id}`}
              disabled={!canCapture}
            >
              undo
            </button>
          </div>
        ) : (
          <div className="relative">
            {param.target?.kind === "yes_no" ? (
              <select
                value={row.value === "" || row.value == null ? "" : String(Number(row.value))}
                onFocus={(e) => {
                  if (isCommitted && onRequestEdit) {
                    e.target.blur();
                    onRequestEdit(param, capturedInfo);
                  }
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({ value: v });
                  if (v !== "" && !isCommitted) onCommit?.({ value: v });
                }}
                disabled={!canCapture}
                className="hv-input w-full text-sm"
                data-testid={`perf-param-yesno-${param.id}`}
              >
                <option value="">Select…</option>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </select>
            ) : (
            <input
              type="text"
              inputMode="decimal"
              value={row.value}
              onChange={(e) => onChange({ value: e.target.value })}
              onFocus={(e) => {
                // Iter 414.b — Tap-committed → confirm → edit-request flow.
                // If this parameter has a committed reading today, hijack
                // the focus and open the confirmation modal instead of
                // letting the user silently overwrite.
                if (isCommitted && onRequestEdit) {
                  e.target.blur();
                  onRequestEdit(param, capturedInfo);
                }
              }}
              onBlur={() => {
                // Iter 418 (Dirk, 2026-02-28) — Auto-commit on blur.
                // Fires POST /perf/readings with just this one line if
                // the value is non-empty AND the row hasn't already
                // been committed today. Turns the row green (LOGGED)
                // instantly on success.
                if (isCommitted) return;
                if (row.value === "" || row.value === null || row.value === undefined) return;
                onCommit?.({});
              }}
              onKeyDown={(e) => {
                // Enter also commits + advances focus to next row's value input.
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                  // Focus the next un-committed input if we can find one.
                  setTimeout(() => {
                    const inputs = Array.from(document.querySelectorAll('input[data-perf-value="1"]'));
                    const idx = inputs.findIndex((el) => el === e.currentTarget);
                    for (let i = idx + 1; i < inputs.length; i++) {
                      if (!inputs[i].readOnly && !inputs[i].disabled) { inputs[i].focus(); break; }
                    }
                  }, 60);
                }
              }}
              readOnly={isCommitted}
              placeholder={isCommitted ? "Tap to edit (locked)" : "—"}
              className={`hv-input w-full font-mono text-sm ${isCommitted ? "cursor-pointer" : ""}`}
              data-testid={`perf-param-input-${param.id}`}
              data-perf-value="1"
              disabled={!canCapture}
            />
            )}
            {committing && (
              <span
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-[#1fb6c1] flex items-center gap-1"
                data-testid={`perf-param-saving-${param.id}`}
              >
                <Loader2 className="w-3 h-3 animate-spin" /> saving
              </span>
            )}
          </div>
        )}
        {/* Iter 414.b — Skip-with-reason picker per row. Preset options:
            Component standby / Component faulty / Free text. Only shown
            when the row is neither committed nor already skipped. */}
        {!isCommitted && !isSkipped && canCapture && (
          <SkipPicker
            paramId={param.id}
            onSkip={(reason) => {
              onChange({ value: "", skipped: true, skip_reason: reason });
              // Iter 418 — auto-commit the skip immediately so the row
              // flips to LOGGED (skipped) without a global Save click.
              setTimeout(() => { onCommit?.({ skipped: true, skip_reason: reason }); }, 30);
            }}
          />
        )}
      </div>
      <div className="mb-2 md:mb-0 relative">
        <textarea
          value={row.notes || ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Notes (optional)"
          rows={2}
          className="hv-input w-full text-sm resize-none pr-9"
          data-testid={`perf-param-notes-${param.id}`}
          disabled={!canCapture || !param.notes_allowed}
        />
        {canCapture && param.notes_allowed && (
          <button
            type="button"
            onClick={cap.recording ? cap.stop : cap.start}
            className={`absolute right-1.5 top-1.5 p-1.5 rounded-md ${cap.recording ? "bg-rose-500/25 text-rose-200 animate-pulse" : "bg-white/[0.05] text-[#a0aabf] hover:text-white"}`}
            title={cap.recording ? "Stop recording" : "Dictate notes"}
            data-testid={`perf-param-mic-${param.id}`}
          >
            {cap.uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mic className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
      <div className="flex flex-col items-stretch md:items-end gap-1">
        {canCapture && (
          <>
            {/* Iter 418.bg (Dirk, 2026-02-28) — swapped plain <input> for
                PhotoPickerButton so Android techs get the "Take photo /
                Choose from gallery" menu. Android's default file picker
                only exposes the gallery — the tech has no way to shoot
                a fresh reading photo. */}
            <PhotoPickerButton
              onFiles={onPickFiles}
              multiple
              accept="image/*"
              disabled={uploading}
              className="hv-btn hv-btn-ghost text-xs inline-flex items-center gap-1"
              title="Attach photo(s) to this reading"
              ariaLabel="Add photo to reading"
              testId={`perf-param-photo-btn-${param.id}`}
            >
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
              {(row.photo_ids || []).length ? `${row.photo_ids.length} attached` : "Add photo"}
            </PhotoPickerButton>
          </>
        )}
        {(row.photo_ids || []).length > 0 && (
          <div className="flex flex-wrap gap-1 justify-end">
            {(row.photo_ids || []).map((pid) => (
              <img
                key={pid}
                src={uploadUrl(pid)}
                alt=""
                className="w-8 h-8 object-cover rounded border border-white/10"
                onError={(e) => { e.target.style.display = "none"; }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  if (!status) return null;
  const map = {
    ok: { c: "#10b981", Icon: CheckCircle2, label: "OK" },
    warn: { c: "#f59e0b", Icon: AlertTriangle, label: "WARN" },
    alarm: { c: "#f43f5e", Icon: AlertOctagon, label: "ALARM" },
  };
  const m = map[status] || { c: "#647087", Icon: HelpCircle, label: "?" };
  return (
    <span
      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-bold inline-flex items-center gap-0.5"
      style={{ background: `${m.c}1a`, color: m.c, border: `1px solid ${m.c}55` }}
    >
      <m.Icon className="w-2.5 h-2.5" /> {m.label}
    </span>
  );
}

/**
 * Iter 389 — Suspicious value guardrail modal.
 * When the user saves and any line has value==0 (on a positive-lower-bound
 * param) or > 50% outside target, we intercept. The user chooses per-row:
 *   • Confirm value (keep + save)
 *   • Mark as not running (skip — no reading saved for that param)
 * The "Skip all" and "Confirm all" shortcuts handle bulk cases.
 */
function SuspiciousValueModal({ items, onResolve, onCancel }) {
  // Default all zero-off items to "skip", all OOR items to "confirm".
  const [decisions, setDecisions] = useState(() => {
    const init = {};
    items.forEach((it) => {
      init[it.pid] = it.reason === "zero-off" ? "skip" : "confirm";
    });
    return init;
  });
  const set = (pid, val) => setDecisions((s) => ({ ...s, [pid]: val }));
  const submit = () => {
    const skipPids = Object.entries(decisions).filter(([, v]) => v === "skip").map(([k]) => k);
    onResolve(skipPids);
  };
  return (
    <div
      className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="perf-suspicious-modal"
      onClick={onCancel}
    >
      <div
        className="hv-card max-w-lg w-full p-4 space-y-3 border-amber-400/40 bg-[#0f1420]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <div className="text-sm font-semibold text-white">Confirm suspicious readings</div>
            <div className="text-[11px] text-[#a0aabf] mt-0.5">
              These values look off. If the equipment was not running, mark as{" "}
              <b className="text-white">Not running</b> — nothing will be saved for that parameter.
              You can log it later.
            </div>
          </div>
        </div>
        <div className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1">
          {items.map((it) => (
            <div
              key={it.pid}
              className="flex items-center justify-between gap-2 p-2 rounded border border-white/5 bg-black/20"
              data-testid={`perf-suspicious-item-${it.pid}`}
            >
              <div className="min-w-0">
                <div className="text-xs text-white truncate">
                  {it.name} <span className="text-[#647087]">{it.unit || ""}</span>
                </div>
                <div className="text-[10px] text-[#a0aabf]">
                  Value <b className="text-white font-mono">{it.value}</b>{" "}
                  {it.reason === "zero-off" ? (
                    <span className="text-amber-300">— zero on a positive-min param</span>
                  ) : (
                    <span className="text-amber-300">— {Math.round(it.outsideBy || 0)}% outside target</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={() => set(it.pid, "skip")}
                  className={`text-[10px] px-2 py-1 rounded-full border transition ${decisions[it.pid] === "skip" ? "border-amber-400 bg-amber-400/15 text-amber-300" : "border-white/10 text-[#a0aabf] hover:border-white/20"}`}
                  data-testid={`perf-suspicious-skip-${it.pid}`}
                >
                  <PowerOff className="w-3 h-3 inline mr-1" /> Not running
                </button>
                <button
                  type="button"
                  onClick={() => set(it.pid, "confirm")}
                  className={`text-[10px] px-2 py-1 rounded-full border transition ${decisions[it.pid] === "confirm" ? "border-emerald-400 bg-emerald-400/15 text-emerald-300" : "border-white/10 text-[#a0aabf] hover:border-white/20"}`}
                  data-testid={`perf-suspicious-confirm-${it.pid}`}
                >
                  Confirm value
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-1 border-t border-white/5">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDecisions(Object.fromEntries(items.map((it) => [it.pid, "skip"])))}
              className="text-[10px] text-[#647087] hover:text-white"
              data-testid="perf-suspicious-skip-all"
            >
              Skip all
            </button>
            <span className="text-[#647087]">·</span>
            <button
              type="button"
              onClick={() => setDecisions(Object.fromEntries(items.map((it) => [it.pid, "confirm"])))}
              className="text-[10px] text-[#647087] hover:text-white"
              data-testid="perf-suspicious-confirm-all"
            >
              Confirm all
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="perf-suspicious-cancel"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="perf-suspicious-submit"
            >
              Continue save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


/**
 * DraftGate — Iter 396.
 * Renders the "Start capturing" / "Continue draft" prompt shown on top
 * of the log sheet when captureMode === "locked". If a server-side
 * draft exists, offers to resume it with a summary (N params filled,
 * saved HH:MM); otherwise shows a single Start CTA.
 */
function ReadingEditRequestModal({ param, capturedInfo, step, onProceed, onClose, onSubmitted }) {
  // Iter 414.b (Dirk, 2026-02-28) — Two-step modal:
  //   step 1 "confirm": short prompt reminding the user that the value
  //     is locked, with proceed/cancel. Matches the user's ask:
  //     "This reading has already been locked for today. Do you want
  //      to edit?"
  //   step 2 "form": collects new value, reason, and (required) photo,
  //     then POSTs to /perf/readings/edit-request — the SAME endpoint
  //     the Analytics register uses.
  // Iter 419.m (Dirk, 2026-02-28) — Added Void flow. Same modal now
  // exposes a "Void this reading" button that hits
  // /perf/readings/{rid}/void with a mandatory reason. Voided readings
  // stay in the audit trail but drop from completeness metrics.
  const readingId = capturedInfo?.reading_line_id
    || capturedInfo?.id
    || capturedInfo?.reading_id;
  const [newValue, setNewValue] = useState(String(capturedInfo?.value ?? ""));
  const [reason, setReason] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [mode, setMode] = useState("edit"); // "edit" or "void"
  const [voidReason, setVoidReason] = useState("");
  const [voidConfirm, setVoidConfirm] = useState(false);

  const submitVoid = async () => {
    setErr("");
    if (!readingId) { setErr("Missing reading id — refresh the page."); return; }
    if (!voidReason.trim() || voidReason.trim().length < 4) {
      setErr("A reason (min 4 chars) is required to void.");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/perf/readings/${readingId}/void`, {
        reason: voidReason.trim(),
      });
      toast.success("Reading voided — removed from completeness, audit trail kept.");
      onSubmitted();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Void failed.");
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setErr("");
    if (!readingId) { setErr("Missing reading id — refresh the page."); return; }
    if (newValue === "" || Number.isNaN(Number(newValue))) { setErr("Enter a numeric new value."); return; }
    if (!reason.trim()) { setErr("Reason is required for the audit log."); return; }
    if (!photo) { setErr("A proof photo is required."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", photo);
      fd.append("kind", "perf_edit_proof");
      const up = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadId = up.data?.id;
      if (!uploadId) throw new Error("Photo upload failed.");
      await api.post("/perf/readings/edit-request", {
        reading_line_id: readingId,
        new_value: Number(newValue),
        verification_photo_upload_id: uploadId,
        reason: reason.trim(),
      });
      toast.success("Edit request submitted for approval.");
      onSubmitted();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Submit failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="perf-capture-edit-request-modal"
    >
      <div className="hv-card w-full max-w-md p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1">
              Reading locked for today
            </div>
            <div className="text-sm font-bold truncate">{param?.name}</div>
            <div className="text-[10px] text-[#647087]">
              current value: <span className="font-mono text-[#a0aabf]">{capturedInfo?.value ?? "—"} {param?.unit || ""}</span>
              {capturedInfo?.captured_by_name && (
                <> · by {capturedInfo.captured_by_name}
                  {capturedInfo?.captured_at ? ` at ${capturedInfo.captured_at.slice(11, 16)}` : ""}
                </>
              )}
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-white/5 text-[#a0aabf]"
                  data-testid="perf-capture-edit-request-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        {step === "confirm" ? (
          <>
            <div className="text-xs text-[#a0aabf]">
              This reading has already been locked for today. Do you want to edit or void it?
              Editing creates an <b>audited edit request</b> (reason + photo required, owner reviews).
              Voiding drops the reading from completeness but keeps the audit trail.
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <div className="flex gap-2">
                <button type="button" onClick={onClose}
                        className="hv-btn hv-btn-ghost flex-1"
                        data-testid="perf-capture-edit-request-cancel">
                  No, keep it
                </button>
                <button type="button" onClick={onProceed}
                        className="hv-btn hv-btn-primary flex-1"
                        data-testid="perf-capture-edit-request-proceed">
                  Edit reading
                </button>
              </div>
              <button
                type="button"
                onClick={() => { setMode("void"); setVoidConfirm(true); setErr(""); }}
                className="hv-btn hv-btn-ghost text-rose-300 hover:!text-rose-200 border border-rose-500/40 hover:!bg-rose-500/10"
                data-testid="perf-capture-void-reading-btn"
              >
                Void this reading
              </button>
            </div>
          </>
        ) : mode === "void" ? (
          <div className="space-y-3" data-testid="perf-capture-void-panel">
            {err && (
              <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2">
                {err}
              </div>
            )}
            <div className="text-[10px] uppercase tracking-wider text-rose-300 font-bold">
              Void reading
            </div>
            <div className="text-xs text-[#a0aabf]">
              This drops the reading from today&apos;s completeness so the
              parameter shows as pending again. The original value
              stays in the audit log with your name + reason.
            </div>
            <label className="block text-xs">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Reason for void *</div>
              <DictTextarea rows={3} value={voidReason}
                        onChange={(e) => setVoidReason(e.target.value)}
                        placeholder="Why is this reading being voided? (min 4 chars)"
                        className="hv-input w-full resize-none"
                        data-testid="perf-capture-void-reason" />
            </label>
            <div className="flex gap-2 pt-1">
              <button type="button"
                      onClick={() => { setMode("edit"); setVoidConfirm(false); setErr(""); }}
                      disabled={busy}
                      className="hv-btn hv-btn-ghost flex-1"
                      data-testid="perf-capture-void-cancel">
                Back
              </button>
              <button type="button" onClick={submitVoid} disabled={busy}
                      className="hv-btn text-xs bg-rose-500/80 hover:bg-rose-500 text-white border border-rose-500/60 flex-1"
                      data-testid="perf-capture-void-submit">
                {busy ? "Voiding…" : "Void reading"}
              </button>
            </div>
          </div>
        ) : (
          <>
            {err && (
              <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded p-2">
                {err}
              </div>
            )}
            <label className="block text-xs">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">New value *</div>
              {param?.target?.kind === "yes_no" ? (
                <select value={newValue} onChange={(e) => setNewValue(e.target.value)}
                        className="hv-input w-full"
                        data-testid="perf-capture-edit-request-value">
                  <option value="">Select…</option>
                  <option value="1">Yes</option>
                  <option value="0">No</option>
                </select>
              ) : (
              <input type="text" inputMode="decimal" value={newValue}
                     onChange={(e) => setNewValue(e.target.value)}
                     className="hv-input w-full font-mono"
                     data-testid="perf-capture-edit-request-value" />
              )}
            </label>
            <label className="block text-xs">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Reason for edit *</div>
              <DictTextarea rows={2} value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why does this reading need to be changed?"
                        className="hv-input w-full resize-none"
                        data-testid="perf-capture-edit-request-reason" />
            </label>
            <label className="block text-xs">
              <div className="text-[10px] uppercase tracking-wider text-[#647087] mb-1">Proof photo *</div>
              <input type="file" accept="image/*" capture="environment"
                     onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                     className="hv-input w-full text-xs"
                     data-testid="perf-capture-edit-request-photo" />
              {photo && (
                <div className="text-[10px] text-emerald-300 mt-1">Photo attached: {photo.name}</div>
              )}
            </label>
            <div className="flex gap-2 pt-1">
              <button type="button" onClick={onClose} disabled={busy}
                      className="hv-btn hv-btn-ghost flex-1"
                      data-testid="perf-capture-edit-request-form-cancel">
                Cancel
              </button>
              <button type="button" onClick={submit} disabled={busy}
                      className="hv-btn hv-btn-primary flex-1"
                      data-testid="perf-capture-edit-request-submit">
                {busy ? "Submitting…" : "Submit for approval"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function SaveStatusPill({ status, draft, onDiscard }) {
  // Iter 414 (Dirk, 2026-02-28) — Persistent save-status indicator.
  // Replaces the old "Start capturing / Continue draft" gate that
  // was silently dropping keystrokes. Users can now type into the
  // form immediately; this pill tells them their work is safe.
  const state = status?.state || "idle";
  const at = status?.at;
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (state !== "saved" || !at) return undefined;
    const iv = setInterval(() => setTick((n) => n + 1), 15000);
    return () => clearInterval(iv);
  }, [state, at, tick]);

  const agoLabel = (() => {
    if (!at) return "";
    try {
      const ms = Date.now() - new Date(at).getTime();
      if (ms < 5000) return "just now";
      if (ms < 60000) return `${Math.floor(ms / 1000)}s ago`;
      if (ms < 3600000) return `${Math.floor(ms / 60000)}m ago`;
      return `${Math.floor(ms / 3600000)}h ago`;
    } catch { return ""; }
  })();

  const { klass, dot, label, title } = (() => {
    switch (state) {
      case "typing":
        return {
          klass: "border-amber-400/40 bg-amber-500/10 text-amber-200",
          dot: "bg-amber-300 animate-pulse",
          label: "Editing…",
          title: "You're typing — autosave will run in a moment.",
        };
      case "saving":
        return {
          klass: "border-[#1FB6C1]/40 bg-[#1FB6C1]/10 text-[#1FB6C1]",
          dot: "bg-[#1FB6C1] animate-pulse",
          label: "Saving…",
          title: "Uploading your draft.",
        };
      case "saved":
        return {
          klass: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
          dot: "bg-emerald-400",
          label: `Saved · ${agoLabel}`,
          title: "Every value you've typed is safely stored as a draft.",
        };
      case "offline":
        return {
          klass: "border-rose-500/40 bg-rose-500/10 text-rose-200",
          dot: "bg-rose-400 animate-pulse",
          label: "Not saved — offline?",
          title: status?.error || "Your last save failed. Will retry when connection returns.",
        };
      default:
        return {
          klass: "border-white/10 bg-white/[0.02] text-[#a0aabf]",
          dot: "bg-[#647087]",
          label: draft ? "Draft loaded — ready" : "Ready — start typing",
          title: draft
            ? "A previous draft has been loaded. Continue where you left off."
            : "Values you type here are autosaved.",
        };
    }
  })();

  return (
    <div
      className={`hv-card border p-2 flex items-center gap-2 flex-wrap ${klass}`}
      data-testid="perf-capture-savepill"
      title={title}
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
      <div className="text-xs font-bold flex-1 min-w-0" data-testid="perf-capture-savepill-label">
        {label}
      </div>
      {draft && draft.last_edited_by_name && (
        <div className="text-[10px] text-[#a0aabf] font-mono">
          last edit · {draft.last_edited_by_name}
        </div>
      )}
      <button
        type="button"
        onClick={onDiscard}
        className="text-[10px] uppercase tracking-wider text-rose-300/70 hover:text-rose-300 border border-rose-400/30 rounded px-1.5 py-0.5"
        data-testid="perf-capture-savepill-discard"
        title="Discard today's typed values without saving"
      >
        Discard
      </button>
    </div>
  );
}


function DraftGate({ draft, equipmentName, onStartFresh, onContinueDraft, onDiscardDraft }) {
  const hasDraft = !!(draft && Array.isArray(draft.lines) && draft.lines.length > 0);
  const filled = hasDraft ? (draft.filled_count ?? draft.lines.filter((ln) => ln.value !== "" && ln.value != null).length) : 0;
  const startedAt = hasDraft ? draft.created_at : null;
  const updatedAt = hasDraft ? draft.updated_at : null;
  const timeLabel = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div
      className="hv-card p-5 border-amber-400/30 bg-gradient-to-br from-amber-500/5 to-transparent"
      data-testid="perf-capture-draft-gate"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-400/30 flex items-center justify-center shrink-0">
          <Lock className="w-4 h-4 text-amber-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-300 font-bold mb-1">
            Log sheet locked
          </div>
          <div className="text-sm text-white font-display font-bold truncate">
            {equipmentName}
          </div>
          <div className="text-xs text-[#a0aabf] mt-1">
            {hasDraft ? (
              <>
                You have a draft with <b className="text-white font-mono">{filled}</b> parameter{filled === 1 ? "" : "s"} filled.
                {" "}Started at <span className="font-mono">{timeLabel(startedAt)}</span>
                {updatedAt && updatedAt !== startedAt && (
                  <> · last saved <span className="font-mono">{timeLabel(updatedAt)}</span></>
                )}
                .
              </>
            ) : (
              <>Click <b className="text-emerald-300">Start capturing</b> to unlock the input fields. Values auto-save as you type.</>
            )}
          </div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {hasDraft && onContinueDraft && (
          <button
            type="button"
            onClick={onContinueDraft}
            className="hv-btn hv-btn-primary text-sm"
            data-testid="perf-capture-continue-draft-btn"
          >
            <Play className="w-4 h-4" /> Continue draft
          </button>
        )}
        <button
          type="button"
          onClick={onStartFresh}
          className={`hv-btn ${hasDraft ? "hv-btn-ghost" : "hv-btn-primary"} text-sm`}
          data-testid="perf-capture-start-btn"
        >
          {hasDraft ? "Discard & start new" : (<><Play className="w-4 h-4" /> Start capturing</>)}
        </button>
        {hasDraft && (
          <button
            type="button"
            onClick={onDiscardDraft}
            className="hv-btn hv-btn-ghost text-[11px] text-rose-300 ml-auto"
            data-testid="perf-capture-discard-draft-btn"
            title="Delete this draft without capturing anything"
          >
            <Trash2 className="w-3 h-3" /> Delete draft only
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * AbortDraftModal — Iter 396.
 * Shown when the user clicks "Save & exit" while there are unsaved
 * keystrokes. Three choices:
 *   • Keep draft & lock — most common, keeps the auto-saved state.
 *   • Discard draft — nukes the server-side draft, resets form.
 *   • Cancel — back to editing.
 */
function AbortDraftModal({ filledCount, totalParams, onSaveExit, onDiscardExit, onCancel }) {
  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-center justify-center p-4" data-testid="perf-capture-abort-modal">
      <div className="hv-card p-5 max-w-md w-full space-y-4 border border-amber-400/40 bg-[#0a0b0e]">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-400/40 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h3 className="text-base font-display font-bold text-white">Exit without saving readings?</h3>
            <p className="text-xs text-[#a0aabf] mt-1">
              You have <b className="text-white font-mono">{filledCount}</b>/{totalParams} parameters filled but not yet committed. Your draft is auto-saved and will still be here when you come back.
            </p>
          </div>
        </div>
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={onSaveExit}
            className="hv-btn hv-btn-primary text-sm"
            data-testid="perf-capture-abort-keep-btn"
          >
            <CheckCircle2 className="w-4 h-4" /> Keep draft & exit
          </button>
          <button
            type="button"
            onClick={onDiscardExit}
            className="hv-btn hv-btn-ghost text-sm text-rose-300"
            data-testid="perf-capture-abort-discard-btn"
          >
            <Trash2 className="w-4 h-4" /> Discard draft & exit
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="text-[11px] text-[#647087] hover:text-white self-center"
            data-testid="perf-capture-abort-cancel-btn"
          >
            Cancel — go back to editing
          </button>
        </div>
      </div>
    </div>
  );
}
